import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import '../styles/ToolsPage.css';
import { computeBestSegment } from './tools/fitCore';

const OFFSET_STRAIN_DEFAULT = 0.002;
const R2_TARGET = 0.995;
const MIN_WINDOW_ABS = 8;

function maybeNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function detectColumnIndices(matrix) {
  const sanitize = (cell) => (typeof cell === 'string' ? cell.replace(/\s+/g, ' ').trim().toLowerCase() : null);

  const headerRow = matrix.find((row) => row.some((cell) => sanitize(cell)));
  if (headerRow) {
    const strainIdx = headerRow.findIndex((cell) => {
      const v = sanitize(cell);
      return v ? /strain/.test(v) : false;
    });
    const stressIdx = headerRow.findIndex((cell) => {
      const v = sanitize(cell);
      return v ? /stress/.test(v) : false;
    });
    if (strainIdx >= 0 && stressIdx >= 0) {
      return { strain: strainIdx, stress: stressIdx };
    }
  }

  const colCount = matrix.reduce((max, row) => Math.max(max, row.length), 0);
  const defaultPairs = [
    { strain: 2, stress: 3 },
    { strain: 0, stress: 1 },
  ];

  const numericCoverage = (col) => {
    let count = 0;
    let numeric = 0;
    for (const row of matrix) {
      if (col >= row.length) continue;
      count++;
      if (maybeNumber(row[col]) !== null) numeric++;
    }
    return count === 0 ? 0 : numeric / count;
  };

  for (const pair of defaultPairs) {
    if (colCount > pair.stress) {
      const covStrain = numericCoverage(pair.strain);
      const covStress = numericCoverage(pair.stress);
      if (covStrain >= 0.5 && covStress >= 0.5) return pair;
    }
  }

  if (colCount === 0) return { strain: 2, stress: 3 };
  const numericScore = Array(colCount).fill(0);
  for (const row of matrix) {
    for (let idx = 0; idx < colCount; idx++) {
      if (maybeNumber(row[idx]) !== null) numericScore[idx] += 1;
    }
  }
  const sorted = numericScore
    .map((score, idx) => ({ idx, score }))
    .sort((a, b) => b.score - a.score);
  if (sorted.length >= 2) {
    const [a, b] = sorted;
    return { strain: Math.min(a.idx, b.idx), stress: Math.max(a.idx, b.idx) };
  }

  return { strain: 2, stress: 3 };
}

function detectStrainIsPercent(matrix, strainColIdx) {
  // 1. Explicit check: look for "%" in the header/unit rows of the strain column
  const checkRows = Math.min(10, matrix.length);
  for (let i = 0; i < checkRows; i++) {
    const cell = matrix[i]?.[strainColIdx];
    if (typeof cell === 'string' && cell.trim() === '%') {
      return true;
    }
  }

  // 2. Heuristic: compute rough elastic modulus from early numeric data.
  //    If modulus < 1000 MPa it's almost certainly percentage-based strain
  //    (real materials have E > 1 GPa even for polymers).
  const numericPairs = [];
  for (const row of matrix) {
    if (numericPairs.length >= 200) break;
    const s = maybeNumber(row?.[strainColIdx]);
    const st = maybeNumber(row?.[strainColIdx === 0 ? 1 : strainColIdx + 1]);
    if (s !== null && st !== null && s > 0) numericPairs.push({ strain: s, stress: st });
  }
  if (numericPairs.length >= 20) {
    const mid = Math.floor(numericPairs.length / 2);
    const roughSlope =
      (numericPairs[mid].stress - numericPairs[0].stress) /
      (numericPairs[mid].strain - numericPairs[0].strain || 1);
    if (roughSlope > 0 && roughSlope < 1000) return true;
  }

  return false;
}

function parseMatrixToPoints(matrix) {
  if (!matrix.length) return [];
  const col = detectColumnIndices(matrix);
  const isPercent = detectStrainIsPercent(matrix, col.strain);
  const scale = isPercent ? 0.01 : 1;
  const rows = [];
  for (const row of matrix) {
    if (!row) continue;
    const strain = maybeNumber(row[col.strain]);
    const stress = maybeNumber(row[col.stress]);
    if (strain === null || stress === null) continue;
    rows.push({ strain: strain * scale, stress });
  }
  return rows;
}

function parseTextToData(text) {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const matrix = [];
  for (const line of lines) {
    const parts = line.split(/[,\s;]+/).filter(Boolean);
    if (!parts.length) continue;
    matrix.push(parts);
  }
  return parseMatrixToPoints(matrix);
}

function linearRegression(points) {
  if (points.length < 2) return null;
  const n = points.length;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2 = 0;
  let sumY2 = 0;

  for (const p of points) {
    sumX += p.strain;
    sumY += p.stress;
    sumXY += p.strain * p.stress;
    sumX2 += p.strain * p.strain;
    sumY2 += p.stress * p.stress;
  }

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX || 1);
  const intercept = (sumY - slope * sumX) / n;

  let ssTot = 0;
  let ssRes = 0;
  const meanY = sumY / n;
  for (const p of points) {
    const fitY = slope * p.strain + intercept;
    ssTot += (p.stress - meanY) ** 2;
    ssRes += (p.stress - fitY) ** 2;
  }
  const r2 = ssTot === 0 ? 1 : 1 - ssRes / ssTot;
  const mse = ssRes / n;
  return { slope, intercept, r2, mse };
}

function findOffsetYieldPoint(data, regression, offsetStrain) {
  if (data.length < 2) return null;
  const offsetLine = (strain) => regression.slope * (strain - offsetStrain) + regression.intercept;
  const diffAt = (p) => p.stress - offsetLine(p.strain);

  for (let i = 0; i < data.length - 1; i++) {
    const p1 = data[i];
    const p2 = data[i + 1];
    const d1 = diffAt(p1);
    const d2 = diffAt(p2);

    if (d1 === 0) return p1;
    if (d1 * d2 <= 0) {
      const t = d1 / (d1 - d2 || 1);
      const strain = p1.strain + t * (p2.strain - p1.strain);
      const stress = p1.stress + t * (p2.stress - p1.stress);
      return { strain, stress };
    }
  }
  return null;
}

function formatNumber(value, digits = 4) {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return value.toFixed(digits);
}

function buildPath(points, xScale, yScale) {
  if (!points.length) return '';
  return points
    .map((p, idx) => {
      const x = xScale(p.strain);
      const y = yScale(p.stress);
      return `${idx === 0 ? 'M' : 'L'}${x},${y}`;
    })
    .join(' ');
}

function niceTicks(min, max, count = 6) {
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
    return [min];
  }
  const span = max - min;
  const step = Math.pow(10, Math.floor(Math.log10(span / count)));
  const err = (count * step) / span;
  const factor = err <= 0.15 ? 10 : err <= 0.35 ? 5 : err <= 0.75 ? 2 : 1;
  const niceStep = step * factor;
  const start = Math.ceil(min / niceStep) * niceStep;
  const end = Math.floor(max / niceStep) * niceStep;
  const ticks = [];
  for (let v = start; v <= end + 1e-12; v += niceStep) {
    ticks.push(Number(v.toFixed(10)));
  }
  return ticks.length ? ticks : [min, max];
}

function ToolsPage() {
  const [data, setData] = useState([]);
  const [fileName, setFileName] = useState('');
  const [manualRange, setManualRange] = useState(null);
  const [offsetStrain, setOffsetStrain] = useState(OFFSET_STRAIN_DEFAULT);
  const [parseError, setParseError] = useState(null);
  const [autoSegment, setAutoSegment] = useState(null);
  const [workerError, setWorkerError] = useState(null);
  const [isComputing, setIsComputing] = useState(false);
  const workerRef = useRef(null);
  const latestDataRef = useRef([]);
  const svgRef = useRef(null);
  const [dragging, setDragging] = useState(null);
  const chartContainerRef = useRef(null);
  const [chartSize, setChartSize] = useState({ width: 960, height: 520 });

  const handleFile = useCallback((file) => {
    const ext = file.name.toLowerCase();
    const reader = new FileReader();

    const finalize = (parsed, raw) => {
      if (!parsed.length) {
        setParseError('No valid strain/stress data found. Please ensure cols 3/4 or the first two cols are strain/stress.');
      } else {
        setParseError(null);
      }
      setData(parsed);
      setFileName(file.name);
      setManualRange(null);
      if (typeof raw === 'string') {
        // keep quiet; raw text is not rendered but preserved for parity with TS logic
      }
    };

    reader.onload = (e) => {
      const payload = e.target?.result ?? null;

      if (ext.endsWith('.xls') || ext.endsWith('.xlsx')) {
        try {
          const workbook = XLSX.read(payload, { type: 'array' });
          const preferredSheetName =
            workbook.SheetNames.find((n) => n.toLowerCase() === 'sheet1') ?? workbook.SheetNames[0];
          const sheet = workbook.Sheets[preferredSheetName];
          const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1 });
          const parsed = parseMatrixToPoints(matrix);
          finalize(parsed, null);
          return;
        } catch (err) {
          setParseError('Failed to parse Excel file. Confirm the file is valid and cols 3/4 are strain/stress.');
          return;
        }
      }

      const text = typeof payload === 'string' ? payload : '';
      const parsed = parseTextToData(text);
      finalize(parsed, text);
    };

    if (ext.endsWith('.xls') || ext.endsWith('.xlsx')) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  }, []);

  useEffect(() => {
    const worker = new Worker(new URL('./tools/fitWorker.js', import.meta.url));
    workerRef.current = worker;
    worker.onmessage = (e) => {
      const payload = e.data;
      if (payload.type === 'result') {
        setAutoSegment(payload.bestSegment ?? null);
        setIsComputing(false);
        setWorkerError(null);
        return;
      }
      setWorkerError(payload.message);
      setIsComputing(false);
      const fallbackMinWin = Math.min(40, Math.max(MIN_WINDOW_ABS, Math.floor(latestDataRef.current.length * 0.02)));
      setAutoSegment(computeBestSegment(latestDataRef.current, R2_TARGET, fallbackMinWin));
    };
    worker.onerror = (err) => {
      console.error('worker error', err);
      setWorkerError('Background worker failed; fell back to main thread calculation.');
      setIsComputing(false);
      const fallbackMinWin = Math.min(40, Math.max(MIN_WINDOW_ABS, Math.floor(latestDataRef.current.length * 0.02)));
      setAutoSegment(computeBestSegment(latestDataRef.current, R2_TARGET, fallbackMinWin));
    };
    return () => {
      worker.terminate();
      workerRef.current = null;
    };
  }, []);

  useEffect(() => {
    latestDataRef.current = data;
  }, [data]);

  useEffect(() => {
    if (!data.length || data.length < MIN_WINDOW_ABS) {
      setAutoSegment(null);
      setIsComputing(false);
      return;
    }
    const minWindow = Math.min(40, Math.max(MIN_WINDOW_ABS, Math.floor(data.length * 0.02)));
    if (workerRef.current) {
      setIsComputing(true);
      setWorkerError(null);
      workerRef.current.postMessage({ data, r2Target: R2_TARGET, minWindow });
    } else {
      setAutoSegment(computeBestSegment(data, R2_TARGET, minWindow));
      setIsComputing(false);
    }
  }, [data]);

  useEffect(() => {
    const resize = () => {
      if (!chartContainerRef.current) return;
      const w = chartContainerRef.current.clientWidth || 960;
      const nextWidth = Math.max(360, Math.min(960, w));
      setChartSize((prev) => (prev.width === nextWidth ? prev : { width: nextWidth, height: 520 }));
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  const activeRange = useMemo(() => {
    if (manualRange) return manualRange;
    if (autoSegment) return { start: autoSegment.start, end: autoSegment.end };
    return null;
  }, [autoSegment, manualRange]);

  const activeRegression = useMemo(() => {
    if (manualRange && manualRange.start >= 0 && manualRange.end < data.length) {
      return linearRegression(data.slice(manualRange.start, manualRange.end + 1));
    }
    if (autoSegment) return autoSegment.regression;
    return null;
  }, [autoSegment, data, manualRange]);

  const yieldPoint = useMemo(() => {
    if (!activeRegression) return null;
    return findOffsetYieldPoint(data, activeRegression, offsetStrain);
  }, [activeRegression, data, offsetStrain]);

  const uts = useMemo(() => {
    if (!data.length) return null;
    return data.reduce((max, p) => (p.stress > max.stress ? p : max), data[0]);
  }, [data]);

  const ductility = useMemo(() => {
    if (!data.length) return null;
    return data[data.length - 1].strain;
  }, [data]);

  const uniformElongation = uts?.strain ?? null;

  const domain = useMemo(() => {
    if (!data.length) {
      return { xMin: 0, xMax: 1, yMin: 0, yMax: 1 };
    }
    const strains = data.map((d) => d.strain);
    const stresses = data.map((d) => d.stress);
    const xMin = Math.min(0, Math.min(...strains));
    const xMax = Math.max(...strains) * 1.05;
    const yMin = Math.min(0, Math.min(...stresses));
    const yMax = Math.max(...stresses) * 1.1;
    return { xMin, xMax: xMax || 1, yMin, yMax: yMax || 1 };
  }, [data]);

  const width = chartSize.width;
  const height = chartSize.height;
  const padding = { top: 24, right: 120, bottom: 48, left: 72 };

  const xScale = useCallback(
    (x) => {
      const span = domain.xMax - domain.xMin || 1;
      return padding.left + ((x - domain.xMin) / span) * (width - padding.left - padding.right);
    },
    [domain.xMax, domain.xMin, width],
  );

  const yScale = useCallback(
    (y) => {
      const span = domain.yMax - domain.yMin || 1;
      return height - padding.bottom - ((y - domain.yMin) / span) * (height - padding.top - padding.bottom);
    },
    [domain.yMax, domain.yMin, height],
  );

  const xTicks = useMemo(() => niceTicks(domain.xMin, domain.xMax, 6), [domain.xMin, domain.xMax, width]);
  const yTicks = useMemo(() => niceTicks(domain.yMin, domain.yMax, 6), [domain.yMin, domain.yMax, height]);

  const xInvert = useCallback(
    (px) => {
      const span = domain.xMax - domain.xMin || 1;
      const ratio = (px - padding.left) / (width - padding.left - padding.right);
      return domain.xMin + ratio * span;
    },
    [domain.xMax, domain.xMin, width],
  );

  const findNearestIndex = useCallback(
    (strainTarget) => {
      if (!data.length) return 0;
      let bestIdx = 0;
      let bestDiff = Math.abs(data[0].strain - strainTarget);
      for (let i = 1; i < data.length; i++) {
        const diff = Math.abs(data[i].strain - strainTarget);
        if (diff < bestDiff) {
          bestDiff = diff;
          bestIdx = i;
        }
      }
      return bestIdx;
    },
    [data],
  );

  const svgXFromEvent = useCallback(
    (e) => {
      if (!svgRef.current) return null;
      const ctm = svgRef.current.getScreenCTM();
      if (!ctm) return null;
      const pt = new DOMPoint(e.clientX, e.clientY).matrixTransform(ctm.inverse());
      return pt.x;
    },
    [],
  );

  const fitLinePoints = useMemo(() => {
    if (!activeRegression || !activeRange || !data.length) return [];
    const { slope, intercept } = activeRegression;
    const x1 = data[activeRange.start]?.strain ?? domain.xMin;
    const segEnd = data[activeRange.end]?.strain ?? domain.xMax;
    const x2 = Math.min(domain.xMax, segEnd + (segEnd - x1) * 0.5);
    return [
      { strain: x1, stress: slope * x1 + intercept },
      { strain: x2, stress: slope * x2 + intercept },
    ];
  }, [activeRegression, activeRange, data, domain.xMax, domain.xMin]);

  const offsetLinePoints = useMemo(() => {
    if (!activeRegression || !activeRange || !data.length) return [];
    const { slope, intercept } = activeRegression;
    const segEnd = data[activeRange.end]?.strain ?? domain.xMax;
    const segStart = data[activeRange.start]?.strain ?? domain.xMin;
    const x1 = offsetStrain;
    const x2 = Math.min(domain.xMax, segEnd + (segEnd - segStart) * 0.5 + offsetStrain);
    return [
      { strain: x1, stress: slope * (x1 - offsetStrain) + intercept },
      { strain: x2, stress: slope * (x2 - offsetStrain) + intercept },
    ];
  }, [activeRegression, domain.xMax, domain.xMin, offsetStrain]);

  const markers = [
    { label: 'Yield', point: yieldPoint, color: '#e44d26' },
    { label: 'UTS', point: uts, color: '#1f8ef1' },
  ].filter((m) => m.point);

  const canRenderChart = data.length > 1;
  const handleStartPoint = activeRange ? data[activeRange.start] : null;
  const handleEndPoint = activeRange ? data[activeRange.end] : null;

  return (
    <div className="tools-page">
      <div className="tools-header">
        <div className="tools-badge">MATAI Tools</div>
        <h1 className="tools-title">Offset Yield Strength</h1>
        <p className="tools-subtitle">
          Upload stress-strain data (defaults to col 3 = Strain, col 4 = Stress; supports .xls/.xlsx/.csv/.txt). The tool
          auto-detects the longest elastic segment, shifts 0.2% to find yield strength, and lets you fine-tune with sliders
          or drag handles on the chart.
        </p>
      </div>

      <div className="tools-layout">
        <div className="tools-card">
          <div className="tools-section">
            <label className="tools-label">Upload data</label>
            <div className="tools-upload">
              <input
                id="tools-file"
                className="tools-file-input"
                type="file"
                accept=".csv,.txt,.xls,.xlsx"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFile(file);
                }}
              />
              <div className="tools-file-ui">
                <label className="tools-file-button" htmlFor="tools-file">
                  Choose file
                </label>
                <span className="tools-file-name">{fileName ? fileName : 'No file selected'}</span>
              </div>
              <p className="tools-hint">
                Reads cols 3/4 or the first two columns as strain/stress; scientific notation supported.{' '}
                {fileName ? `Loaded: ${fileName}` : 'No file selected yet.'}
              </p>
              {parseError ? <p className="tools-error">{parseError}</p> : null}
              {workerError ? <p className="tools-error">{workerError}</p> : null}
            </div>
          </div>

          <div className="tools-section">
            <label className="tools-label">0.2% offset (engineering strain)</label>
            <input
              className="tools-input"
              type="number"
              step="0.0001"
              min="0"
              value={offsetStrain}
              onChange={(e) => setOffsetStrain(Number(e.target.value) || 0)}
            />
            <p className="tools-hint">Default 0.002 = 0.2%. The offset line stays parallel to the elastic fit.</p>
          </div>

          <div className="tools-section">
            <div className="tools-range-header">
              <span>Elastic segment range</span>
              <button className="tools-link" onClick={() => setManualRange(null)} disabled={!manualRange}>
                Reset to auto
              </button>
            </div>
            {canRenderChart && activeRange ? (
              <>
                <div className="tools-slider">
                  <span>Start</span>
                  <input
                    type="range"
                    min={0}
                    max={data.length - 2}
                    value={activeRange.start}
                    onChange={(e) => {
                      const nextStart = Number(e.target.value);
                      const end = Math.max(nextStart + 2, activeRange.end);
                      setManualRange({ start: nextStart, end });
                    }}
                  />
                  <span className="tools-slider-value">{activeRange.start}</span>
                </div>
                <div className="tools-slider">
                  <span>End</span>
                  <input
                    type="range"
                    min={1}
                    max={data.length - 1}
                    value={activeRange.end}
                    onChange={(e) => {
                      const nextEnd = Number(e.target.value);
                      const start = Math.min(nextEnd - 2, activeRange.start);
                      setManualRange({ start, end: nextEnd });
                    }}
                  />
                  <span className="tools-slider-value">{activeRange.end}</span>
                </div>
                <p className="tools-hint">
                  Auto range: [{autoSegment?.start ?? '-'} ~ {autoSegment?.end ?? '-'}], R²≈
                  {formatNumber(autoSegment?.regression?.r2, 4)} {isComputing ? '(computing...)' : ''}
                </p>
              </>
            ) : (
              <p className="tools-hint">
                After upload, the tool auto-detects a linear segment. Use sliders or drag the "Start/End" handles to adjust.
              </p>
            )}
          </div>
        </div>

        <div className="tools-card">
          {canRenderChart ? (
            <div className="tools-chart" ref={chartContainerRef}>
              <svg
                ref={svgRef}
                width={width}
                height={height}
                viewBox={`0 0 ${width} ${height}`}
                preserveAspectRatio="xMidYMid meet"
                style={{ width: '100%', height: 'auto' }}
                onPointerDown={(e) => {
                  e.preventDefault();
                }}
                onPointerMove={(e) => {
                  if (!dragging || !activeRange || !svgRef.current) return;
                  const svgX = svgXFromEvent(e);
                  if (svgX === null) return;
                  const targetStrain = xInvert(svgX) + dragging.strainOffset;
                  const idx = findNearestIndex(targetStrain);
                  const minGap = 2;
                  if (dragging.handle === 'start') {
                    const newStart = Math.min(idx, activeRange.end - minGap);
                    setManualRange({ start: newStart, end: activeRange.end });
                  } else {
                    const newEnd = Math.max(idx, activeRange.start + minGap);
                    setManualRange({ start: activeRange.start, end: newEnd });
                  }
                }}
                onPointerUp={(e) => {
                  if (svgRef.current) {
                    try {
                      svgRef.current.releasePointerCapture(e.pointerId);
                    } catch (err) {
                      // ignore
                    }
                  }
                  setDragging(null);
                }}
                onPointerLeave={() => { if (!dragging) return; /* keep dragging if pointer captured */ }}
              >
                <defs>
                  <linearGradient id="gridFill" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#f7f8fa" />
                    <stop offset="100%" stopColor="#eef1f7" />
                  </linearGradient>
                  <clipPath id="chartClip">
                    <rect
                      x={padding.left}
                      y={padding.top}
                      width={width - padding.left - padding.right}
                      height={height - padding.top - padding.bottom}
                    />
                  </clipPath>
                </defs>
                <rect
                  x={padding.left}
                  y={padding.top}
                  width={width - padding.left - padding.right}
                  height={height - padding.top - padding.bottom}
                  fill="url(#gridFill)"
                  stroke="#dce1eb"
                  strokeWidth={1}
                  rx={8}
                />

                <g className="axes">
                  <text x={padding.left - 10} y={padding.top + 12} fill="#6b7280" fontSize={12} textAnchor="end">
                    Stress
                  </text>
                  <text
                    x={(width + padding.left - padding.right) / 2}
                    y={height - 8}
                    fill="#6b7280"
                    fontSize={12}
                    textAnchor="middle"
                  >
                    Strain
                  </text>
                  {yTicks.map((tick) => {
                    const y = yScale(tick);
                    return (
                      <g key={`y-${tick}`}>
                        <line
                          x1={padding.left - 6}
                          x2={width - padding.right}
                          y1={y}
                          y2={y}
                          stroke="#e5e7eb"
                          strokeWidth={1}
                          strokeDasharray="2 4"
                        />
                        <text x={padding.left - 10} y={y + 4} fill="#6b7280" fontSize={11} textAnchor="end">
                          {formatNumber(tick, 3)}
                        </text>
                      </g>
                    );
                  })}
                  {xTicks.map((tick) => {
                    const x = xScale(tick);
                    return (
                      <g key={`x-${tick}`}>
                        <line
                          x1={x}
                          x2={x}
                          y1={padding.top}
                          y2={height - padding.bottom + 6}
                          stroke="#e5e7eb"
                          strokeWidth={1}
                          strokeDasharray="2 4"
                        />
                        <text x={x} y={height - padding.bottom + 18} fill="#6b7280" fontSize={11} textAnchor="middle">
                          {formatNumber(tick, 3)}
                        </text>
                      </g>
                    );
                  })}
                </g>

                <path
                  d={buildPath(data, xScale, yScale)}
                  fill="none"
                  stroke="#334155"
                  strokeWidth={2}
                  strokeLinecap="round"
                />

                {fitLinePoints.length ? (
                  <path
                    d={buildPath(fitLinePoints, xScale, yScale)}
                    fill="none"
                    stroke="#f97316"
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    clipPath="url(#chartClip)"
                  />
                ) : null}

                {offsetLinePoints.length ? (
                  <path
                    d={buildPath(offsetLinePoints, xScale, yScale)}
                    fill="none"
                    stroke="#10b981"
                    strokeWidth={1.6}
                    strokeDasharray="4 4"
                    clipPath="url(#chartClip)"
                  />
                ) : null}

                {markers.map((m) => (
                  <g key={m.label}>
                    <circle
                      cx={xScale(m.point.strain)}
                      cy={yScale(m.point.stress)}
                      r={5}
                      fill={m.color}
                      stroke="#ffffff"
                      strokeWidth={1.8}
                    />
                    <text x={xScale(m.point.strain) + 8} y={yScale(m.point.stress) - 8} fill={m.color} fontSize={11}>
                      {m.label}
                    </text>
                  </g>
                ))}

                {handleStartPoint ? (
                  <g
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (svgRef.current) {
                        try { svgRef.current.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ }
                      }
                      const svgX = svgXFromEvent(e);
                      const handleStrain = handleStartPoint.strain;
                      const cursorStrain = svgX !== null ? xInvert(svgX) : handleStrain;
                      setDragging({ handle: 'start', strainOffset: handleStrain - cursorStrain });
                    }}
                    style={{ cursor: 'grab' }}
                  >
                    <circle
                      cx={xScale(handleStartPoint.strain)}
                      cy={yScale(handleStartPoint.stress)}
                      r={12}
                      fill="rgba(249,115,22,0.18)"
                      stroke="#f97316"
                      strokeWidth={2}
                    />
                    <text
                      x={xScale(handleStartPoint.strain)}
                      y={yScale(handleStartPoint.stress) - 16}
                      fill="#f97316"
                      fontSize={11}
                      textAnchor="middle"
                    >
                      Start
                    </text>
                  </g>
                ) : null}

                {handleEndPoint ? (
                  <g
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (svgRef.current) {
                        try { svgRef.current.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ }
                      }
                      const svgX = svgXFromEvent(e);
                      const handleStrain = handleEndPoint.strain;
                      const cursorStrain = svgX !== null ? xInvert(svgX) : handleStrain;
                      setDragging({ handle: 'end', strainOffset: handleStrain - cursorStrain });
                    }}
                    style={{ cursor: 'grab' }}
                  >
                    <circle
                      cx={xScale(handleEndPoint.strain)}
                      cy={yScale(handleEndPoint.stress)}
                      r={12}
                      fill="rgba(31,142,241,0.18)"
                      stroke="#1f8ef1"
                      strokeWidth={2}
                    />
                    <text
                      x={xScale(handleEndPoint.strain)}
                      y={yScale(handleEndPoint.stress) - 16}
                      fill="#1f8ef1"
                      fontSize={11}
                      textAnchor="middle"
                    >
                      End
                    </text>
                  </g>
                ) : null}
              </svg>
            </div>
          ) : (
            <div className="tools-empty">Upload data to render the curve</div>
          )}
        </div>
      </div>

      <div className="tools-stats-grid">
        <div className="tools-stat">
          <p className="tools-stat-label">Offset Yield Strength</p>
          <p className="tools-stat-value">
            {formatNumber(yieldPoint?.stress, 2)}
            <span className="tools-stat-sub">@ ε={formatNumber(yieldPoint?.strain, 4)}</span>
          </p>
        </div>
        <div className="tools-stat">
          <p className="tools-stat-label">UTS (max stress)</p>
          <p className="tools-stat-value">
            {formatNumber(uts?.stress, 2)}
            <span className="tools-stat-sub">ε={formatNumber(uts?.strain, 4)}</span>
          </p>
        </div>
        <div className="tools-stat">
          <p className="tools-stat-label">Ductility (final strain)</p>
          <p className="tools-stat-value">{formatNumber(ductility, 4)}</p>
        </div>
        <div className="tools-stat">
          <p className="tools-stat-label">Uniform Elongation</p>
          <p className="tools-stat-value">{formatNumber(uniformElongation, 4)}</p>
        </div>
      </div>

      <div className="tools-card tools-algo">
        <div>
          <p className="tools-label">Fit parameters</p>
          {activeRegression ? (
            <>
              <p className="tools-eq">
                σ = {formatNumber(activeRegression.intercept, 4)} + {formatNumber(activeRegression.slope, 4)} · ε
              </p>
              <p className="tools-hint">
                R² = {formatNumber(activeRegression.r2, 4)} | MSE = {formatNumber(activeRegression.mse, 4)}
              </p>
              <p className="tools-hint">Young&apos;s modulus (slope) ≈ {formatNumber(activeRegression.slope, 2)}</p>
            </>
          ) : (
            <p className="tools-hint">Waiting for data ...</p>
          )}
        </div>
        <div className="tools-algo-notes">
          <p className="tools-label">Algorithm notes</p>
          <ul>
            <li>Elastic segment: tracks the steepest slope; continues while R² ≥ 0.995 and slope drop ≤ 5%, otherwise stops.</li>
            <li>0.2% offset: shifts the elastic line by the offset (default 0.002) and intersects with the curve via linear interpolation.</li>
            <li>UTS: max of stress column; Uniform Elongation uses the strain at UTS; Ductility uses the last strain point.</li>
            <li>Manual override: drag the Start/End sliders or grab the handles on the chart to adjust.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default ToolsPage;
